import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { MessageSquare, SquarePen } from 'lucide-react';
import { keys, portalQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDateTime, messageCreateSchema, messageReplySchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Badge,
  Button,
  DocumentList,
  EmptyState,
  ErrorState,
  FileDropzone,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRootError,
  InputField,
  Select,
  Skeleton,
  StatusBadge,
  TextareaField,
  toast,
  cn,
} from '@blimo/ui';
import { unitTitle } from '../lib/units.js';
import { PageHeader } from '../components/ui/page-header.jsx';
import { ItemList, ItemRow, Section } from '../components/ui/section.jsx';
import { Pager } from '../components/ui/pager.jsx';

const ACCEPT = 'image/*,application/pdf';
const FILE_HINT = 'Fotos oder PDF, bis zu 10 Dateien à 20 MB.';

/** "Lindenhof · WE 01" */
const threadContext = (thread) => [thread.property?.name, thread.unit ? unitTitle(thread.unit) : null].filter(Boolean).join(' · ');

const BACK = { to: '/nachrichten', search: { page: undefined }, label: 'Nachrichten' };

/* ------------------------------------------------------------------ Liste */

export function MessagesPage() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const page = search.page ?? 1;
  const query = useQuery(portalQueries.messages(api, { page }));
  const lastPage = Math.max(1, query.data?.meta?.last_page ?? 1);
  const goTo = (next) => navigate({ to: '/nachrichten', search: { page: next > 1 ? next : undefined } });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Nachrichten"
        description="Ihre Anliegen an die Verwaltung und deren Antworten."
        actions={
          <Button asChild>
            <Link to="/nachrichten/neu" search={{ unit: undefined }}>
              <SquarePen aria-hidden="true" /> Neue Nachricht
            </Link>
          </Button>
        }
      />
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : query.data.data.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="Noch keine Nachrichten"
          description="Schreiben Sie der Verwaltung zu einer Ihrer Einheiten – die Antwort erscheint hier."
          action={
            <Button asChild>
              <Link to="/nachrichten/neu" search={{ unit: undefined }}>
                Nachricht schreiben
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <ItemList aria-label="Nachrichten" className={cn(query.isPlaceholderData && 'opacity-60')}>
            {query.data.data.map((thread) => (
              <li key={thread.id}>
                <ItemRow
                  as={Link}
                  to="/nachrichten/$threadId"
                  params={{ threadId: String(thread.id) }}
                  icon={MessageSquare}
                  title={
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={thread.has_unread_reply ? 'font-semibold' : 'font-normal'}>{thread.subject}</span>
                      {thread.has_unread_reply && <Badge variant="info">Neue Antwort</Badge>}
                    </span>
                  }
                  subtitle={
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>{[threadContext(thread), thread.last_message_at ? formatDateTime(thread.last_message_at) : null].filter(Boolean).join(' · ')}</span>
                      <StatusBadge domain="messageThread" status={thread.status} />
                    </span>
                  }
                />
              </li>
            ))}
          </ItemList>
          <Pager page={page} lastPage={lastPage} onPage={goTo} />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Neue Nachricht */

export function NewMessagePage() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false });
  const properties = useQuery(portalQueries.properties(api));
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const form = useForm({
    resolver: zodResolver(messageCreateSchema),
    defaultValues: { unit_id: search.unit ? String(search.unit) : '', subject: '', body: '' },
  });
  // Only currently owned units (`is_owned` from the API) – the API answers 404 for others anyway.
  const groups = (properties.data ?? []).map((p) => ({ ...p, my_units: (p.my_units ?? []).filter((u) => u.is_owned) })).filter((p) => p.my_units.length > 0);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const thread = await api.portal.messages.create({ unit_id: Number(values.unit_id), subject: values.subject.trim(), body: values.body.trim(), files });
      queryClient.invalidateQueries({ queryKey: keys.portal.resource('messages') });
      if (thread?.id) queryClient.setQueryData(keys.portal.detail('messages', thread.id), thread);
      toast.success('Nachricht gesendet');
      if (thread?.id) navigate({ to: '/nachrichten/$threadId', params: { threadId: String(thread.id) } });
      else navigate({ to: '/nachrichten', search: { page: undefined } });
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['unit_id', 'subject', 'body'] });
    }
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader back={BACK} title="Neue Nachricht" description="Ihre Nachricht geht an die zuständige Verwaltung Ihrer Einheit." />
      {properties.error ? (
        <ErrorState error={properties.error} onRetry={() => properties.refetch()} />
      ) : properties.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : groups.length === 0 ? (
        <EmptyState
          title="Keine Einheiten zugeordnet"
          description="Nachrichten können nur zu Ihren eigenen Einheiten gesendet werden. Bitte wenden Sie sich an Ihre Verwaltung."
        />
      ) : (
        <Form {...form}>
          <form onSubmit={onSubmit} noValidate className="flex max-w-2xl flex-col gap-4">
            <FormRootError />
            <Section title="Ihr Anliegen" bodyClassName="flex flex-col gap-4">
              <FormField
                name="unit_id"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Einheit</FormLabel>
                    <FormControl>
                      <Select {...field} value={field.value ?? ''}>
                        <option value="">Bitte wählen …</option>
                        {groups.map((property) => (
                          <optgroup key={property.id} label={property.name || `Liegenschaft #${property.id}`}>
                            {property.my_units.map((unit) => (
                              <option key={unit.id} value={String(unit.id)}>
                                {unitTitle(unit)}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputField name="subject" label="Betreff" autoComplete="off" />
              <TextareaField name="body" label="Nachricht" rows={6} />
              <FileDropzone files={files} onFilesChange={setFiles} label="Anhänge" accept={ACCEPT} camera maxFiles={10} hint={FILE_HINT} />
            </Section>
            <Button type="submit" className="w-full sm:w-fit" loading={form.formState.isSubmitting}>
              Nachricht senden
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Verlauf */

export function MessageThreadPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { threadId } = useParams({ strict: false });
  const { data: thread, error, isPending, refetch } = useQuery(portalQueries.message(api, threadId));

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Nachricht" back={BACK} />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Nachricht nicht gefunden" />
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label="Wird geladen">
        <PageHeader title="Nachricht" back={BACK} />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        back={BACK}
        title={thread.subject}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {threadContext(thread)}
            <StatusBadge domain="messageThread" status={thread.status} />
          </span>
        }
      />
      <ol aria-label="Verlauf" className="flex max-w-3xl flex-col gap-3">
        {(thread.messages ?? []).map((message) => {
          const mine = message.direction === 'inbound';
          return (
            <li key={message.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
              <article
                aria-label={`Nachricht von ${mine ? 'Ihnen' : 'der Verwaltung'}`}
                data-mine={mine}
                className="chat-bubble flex w-fit min-w-0 max-w-[88%] flex-col gap-1 sm:max-w-[75%]"
              >
                <header className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{mine ? 'Sie' : (message.author?.name ?? 'Verwaltung')}</span>
                  {!mine && <Badge variant="secondary">Verwaltung</Badge>}
                  <span className="numeric">{formatDateTime(message.created_at)}</span>
                </header>
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{message.body}</p>
                {message.attachments?.length > 0 && (
                  <DocumentList
                    label={`Anhänge der Nachricht vom ${formatDateTime(message.created_at)}`}
                    documents={message.attachments}
                    hrefFor={(doc) => api.portal.messages.attachmentUrl(thread.id, doc.id)}
                    className="mt-1 bg-background"
                  />
                )}
              </article>
            </li>
          );
        })}
      </ol>
      <FollowUpForm
        threadId={thread.id}
        onSaved={(updated) => {
          if (updated?.id) queryClient.setQueryData(keys.portal.detail('messages', thread.id), updated);
          else queryClient.invalidateQueries({ queryKey: keys.portal.detail('messages', thread.id) });
          queryClient.invalidateQueries({ queryKey: [...keys.portal.resource('messages'), 'list'] });
        }}
      />
    </div>
  );
}

function FollowUpForm({ threadId, onSaved }) {
  const api = useApi();
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const form = useForm({ resolver: zodResolver(messageReplySchema), defaultValues: { body: '' } });
  const onSubmit = form.handleSubmit(async ({ body }) => {
    try {
      const updated = await api.portal.messages.followUp(threadId, { body: body.trim(), files });
      form.reset({ body: '' });
      setFiles([]);
      toast.success('Nachricht gesendet');
      onSaved(updated);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['body'] });
    }
  });
  return (
    <Section title="Antworten" className="max-w-3xl">
      <Form {...form}>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
          <FormRootError />
          <TextareaField name="body" label="Ihre Nachricht" rows={4} />
          <FileDropzone files={files} onFilesChange={setFiles} label="Anhänge" accept={ACCEPT} camera maxFiles={10} hint={FILE_HINT} />
          <Button type="submit" className="w-full sm:w-fit" loading={form.formState.isSubmitting}>
            Senden
          </Button>
        </form>
      </Form>
    </Section>
  );
}
